import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import * as d3 from 'd3';
import { getDifficultyStyle, getPurposeStyle, getSourceInfo, formatSearchVolume, intentTypes, lightenColor, difficultyRanges, purposeTypes } from '@/shared/utils/constants';

interface KeywordData {
  keyword: string;
  difficalty?: number | null;
  sv?: number | null;
  intent?: string[];
  purpose?: string[];
  source?: string;
  len?: number;
  parent_keyword?: string;
  [key: string]: any;
}

interface ChartViewProps {
  data: KeywordData[];
  groupBy: string;
  quickFilterText: string;
  language: 'fa' | 'en';
  isDark: boolean;
  isRTL: boolean;
  translations: any;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onDownloadSVGRef?: (fn: () => void) => void;
}

interface ChartDataPoint {
  name: string;
  value: number;
  fill: string;
  groupKey: string;
  keywords: KeywordData[];
  stats?: {
    totalKeywords: number;
    avgDifficulty: number;
    totalSearchVolume: number;
    avgSearchVolume: number;
  };
  keyword?: string;
  difficulty?: number | null;
  searchVolume?: number | null;
  intent?: string[];
  purpose?: string[];
  length?: number;
  parentKeyword?: string;
  source?: string;
  parent?: string;
  children?: ChartDataPoint[];
}

const ChartView: React.FC<ChartViewProps> = ({ data, groupBy, quickFilterText, language, isDark, isRTL, isFullscreen, onToggleFullscreen, onDownloadSVGRef }) => {
  const [hoveredSegment, setHoveredSegment] = useState<ChartDataPoint | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const fullscreenSvgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const fullscreenRef = useRef<HTMLDivElement>(null);

  // Filter data based on quickFilterText (same logic as KeywordDataView)
  const filteredData = useMemo(() => {
    if (!quickFilterText || quickFilterText.trim() === '') return data;
    const filterText = quickFilterText.toLowerCase().trim();
    return data.filter(item => {
      // Search in keyword
      if (item.keyword && item.keyword.toLowerCase().includes(filterText)) {
        return true;
      }
      // Search in parent_keyword
      if (item.parent_keyword && item.parent_keyword.toLowerCase().includes(filterText)) {
        return true;
      }
      // Search in source
      if (item.source && item.source.toLowerCase().includes(filterText)) {
        return true;
      }
      // Search in intent array
      if (Array.isArray(item.intent) && item.intent.some(i => i.toLowerCase().includes(filterText))) {
        return true;
      }
      // Search in purpose array
      if (Array.isArray(item.purpose) && item.purpose.some(p => p.toLowerCase().includes(filterText))) {
        return true;
      }
      return false;
    });
  }, [data, quickFilterText]);

  // Group data based on groupBy (same logic as CardView)
  const groupedData = useMemo<Record<string, KeywordData[]>>(() => {
    const groups: Record<string, KeywordData[]> = {};
    
    filteredData.forEach(item => {
      let groupKey = 'other';
      
      if (groupBy === 'intent') {
        if (item.intent && Array.isArray(item.intent) && item.intent.length > 0) {
          groupKey = item.intent[0];
        } else {
          groupKey = 'no-intent';
        }
      } else if (groupBy === 'difficulty') {
        if (item.difficalty !== null && item.difficalty !== undefined) {
          const style = getDifficultyStyle(item.difficalty);
          groupKey = style ? style.label : 'unknown';
        } else {
          groupKey = 'no-difficulty';
        }
      } else if (groupBy === 'searchVolume') {
        if (item.sv !== null && item.sv !== undefined) {
          if (item.sv < 1000) {
            groupKey = language === 'fa' ? 'کم (زیر 1000)' : 'Low (< 1K)';
          } else if (item.sv < 10000) {
            groupKey = language === 'fa' ? 'متوسط (1K-10K)' : 'Medium (1K-10K)';
          } else if (item.sv < 100000) {
            groupKey = language === 'fa' ? 'زیاد (10K-100K)' : 'High (10K-100K)';
          } else {
            groupKey = language === 'fa' ? 'خیلی زیاد (بالای 100K)' : 'Very High (> 100K)';
          }
        } else {
          groupKey = 'no-volume';
        }
      } else if (groupBy === 'length') {
        groupKey = `${item.len || 0} ${language === 'fa' ? 'کلمه' : 'words'}`;
      } else if (groupBy === 'purpose') {
        if (item.purpose && Array.isArray(item.purpose) && item.purpose.length > 0) {
          const purposeConfig = getPurposeStyle(item.purpose[0]);
          groupKey = purposeConfig ? (language === 'fa' ? purposeConfig.label : purposeConfig.enLabel) : item.purpose[0];
        } else {
          groupKey = 'no-purpose';
        }
      } else if (groupBy === 'source') {
        if (item.source) {
          const sourceConfig = getSourceInfo(item.source);
          groupKey = sourceConfig ? (language === 'fa' ? sourceConfig.fa : sourceConfig.en) : item.source;
        } else {
          groupKey = 'no-source';
        }
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });
    
    return groups;
  }, [filteredData, groupBy, language]);

  // Get group label
  const getGroupLabel = (groupKey: string): string => {
    if (groupBy === 'intent') {
      const intent = intentTypes[groupKey];
      if (intent) {
        return language === 'fa' ? intent.label : intent.enLabel;
      }
      return groupKey === 'no-intent' ? (language === 'fa' ? 'بدون نیت' : 'No Intent') : groupKey;
    } else if (groupBy === 'difficulty') {
      return groupKey === 'no-difficulty' ? (language === 'fa' ? 'بدون سختی' : 'No Difficulty') : groupKey;
    } else if (groupBy === 'searchVolume') {
      return groupKey === 'no-volume' ? (language === 'fa' ? 'بدون حجم جستجو' : 'No Search Volume') : groupKey;
    } else if (groupBy === 'length') {
      return groupKey;
    } else if (groupBy === 'purpose') {
      return groupKey === 'no-purpose' ? (language === 'fa' ? 'بدون هدف' : 'No Purpose') : groupKey;
    } else if (groupBy === 'source') {
      return groupKey === 'no-source' ? (language === 'fa' ? 'بدون منبع' : 'No Source') : groupKey;
    }
    return groupKey;
  };


  // Helper function to get purpose key from label
  const getPurposeKeyFromLabel = (label: string): string | null => {
    for (const [key, config] of Object.entries(purposeTypes)) {
      if (config.label === label || config.enLabel === label) {
        return key;
      }
    }
    return null;
  };

  // Helper function to get difficulty range from label
  const getDifficultyRangeFromLabel = (label: string) => {
    return difficultyRanges.find(range => 
      range.label === label || range.enLabel === label
    );
  };

  // Helper function to get color based on groupBy and groupKey
  const getGroupColor = (groupBy: string, groupKey: string): { inner: string; outer: string } => {
    if (groupBy === 'difficulty') {
      // Use difficulty colors - bgColor for inner ring (light version)
      const difficultyRange = getDifficultyRangeFromLabel(groupKey);
      if (difficultyRange) {
        return {
          inner: difficultyRange.bgColor, // Light color for inner ring
          outer: lightenColor(difficultyRange.bgColor, 30) // Lighter for outer ring
        };
      }
      // Fallback for no-difficulty
      return {
        inner: '#f3f4f6',
        outer: lightenColor('#f3f4f6', 30)
      };
    } else if (groupBy === 'intent') {
      // Use intent colors - bgColor for inner ring
      const intent = intentTypes[groupKey];
      if (intent) {
        return {
          inner: intent.bgColor,
          outer: lightenColor(intent.bgColor, 30)
        };
      }
      // Fallback for no-intent
      return {
        inner: '#f3f4f6',
        outer: lightenColor('#f3f4f6', 30)
      };
    } else if (groupBy === 'purpose') {
      // Use purpose colors - bgColor for inner ring
      // groupKey is the label, need to find the actual key
      const purposeKey = getPurposeKeyFromLabel(groupKey);
      const purpose = purposeKey ? purposeTypes[purposeKey] : null;
      if (purpose) {
        return {
          inner: purpose.bgColor,
          outer: lightenColor(purpose.bgColor, 30)
        };
      }
      // Fallback for no-purpose
      return {
        inner: '#f3f4f6',
        outer: lightenColor('#f3f4f6', 30)
      };
    } else if (groupBy === 'source') {
      // Use source colors - create colors based on source
      const sourceInfo = getSourceInfo(groupKey);
      if (sourceInfo) {
        // Generate a color based on source name hash
        const hash = groupKey.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const hue = hash % 360;
        const baseColor = `hsl(${hue}, 70%, 85%)`;
        return {
          inner: baseColor,
          outer: lightenColor(baseColor, 30)
        };
      }
    }
    
    // Default colors for other groupBy types
    const defaultColors = ['#c4b5fd', '#fdba74', '#fde68a', '#86efac', '#93c5fd', '#f9a8d4', '#c4b5fd', '#5eead4', '#fcd34d', '#6ee7b7'];
    const colorIndex = groupKey.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % defaultColors.length;
    const baseColor = defaultColors[colorIndex];
    return {
      inner: baseColor,
      outer: lightenColor(baseColor, 40)
    };
  };

  // Prepare hierarchical data for Sunburst
  const sunburstData = useMemo<ChartDataPoint>(() => {
    const groups = Object.entries(groupedData);
    
    const children = groups.map(([groupKey, keywords]) => {
      const totalKeywords = keywords.length;
      const avgDifficulty = keywords.reduce((sum, k) => sum + (k.difficalty || 0), 0) / totalKeywords || 0;
      const totalSearchVolume = keywords.reduce((sum, k) => sum + (k.sv || 0), 0);
      const avgSearchVolume = totalSearchVolume / totalKeywords || 0;

      // Get colors based on groupBy
      const colors = getGroupColor(groupBy, groupKey);
      const groupColor = colors.inner; // Light color for inner ring
      const lightenedColor = colors.outer; // Lighter for outer ring

      return {
        name: getGroupLabel(groupKey),
        value: totalKeywords,
        fill: groupColor, // Light color for inner ring
        groupKey,
        keywords,
        stats: {
          totalKeywords,
          avgDifficulty: Math.round(avgDifficulty),
          totalSearchVolume,
          avgSearchVolume: Math.round(avgSearchVolume),
        },
        children: keywords.map((item) => ({
          name: item.keyword,
          value: 1,
          fill: lightenedColor, // Lighter version for outer ring
          keyword: item.keyword,
          difficulty: item.difficalty,
          searchVolume: item.sv,
          intent: item.intent,
          purpose: item.purpose,
          length: item.len,
          parentKeyword: item.parent_keyword,
          source: item.source,
          parent: getGroupLabel(groupKey),
          groupKey: '',
          keywords: [item],
        }))
      };
    });

    return {
      name: 'root',
      value: 0,
      fill: '#fff',
      groupKey: '',
      keywords: [],
      children: children
    };
  }, [groupedData, language, groupBy]);

  // Helper function to draw chart on any SVG element
  const drawChart = useCallback((svgElement: SVGSVGElement | null, width: number, height: number) => {
    if (!svgElement) return;

    const radius = Math.min(width, height) / 2 - 40;

    // Clear previous content
    d3.select(svgElement).selectAll('*').remove();

    const svg = d3.select(svgElement)
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    // Create partition layout
    const partition = d3.partition<ChartDataPoint>()
      .size([2 * Math.PI, radius]);

    // Create hierarchy
    const root = d3.hierarchy(sunburstData)
      .sum(d => d.value);

    // Compute the partition
    partition(root as any);

    // Create arc generators for inner (groups) and outer (keywords) rings
    const arcInner = d3.arc<any>()
      .startAngle((d: any) => d.x0)
      .endAngle((d: any) => d.x1)
      .innerRadius((d: any) => d.y0)
      .outerRadius((d: any) => d.y1);

    const arcOuter = d3.arc<any>()
      .startAngle((d: any) => d.x0)
      .endAngle((d: any) => d.x1)
      .innerRadius((d: any) => d.y0)
      .outerRadius((d: any) => d.y1);

    // Get all nodes (groups and keywords)
    const nodes = root.descendants().filter(d => d.depth > 0);

    // Draw arcs
    g.selectAll('path')
      .data(nodes)
      .enter()
      .append('path')
      .attr('fill', (d: any) => d.data.fill || '#ccc')
      .attr('stroke', isDark ? '#1f2937' : '#ffffff')
      .attr('stroke-width', 2)
      .attr('d', (d: any) => {
        if (d.depth === 1) {
          // Inner ring - groups
          return arcInner(d);
        } else {
          // Outer ring - keywords
          return arcOuter(d);
        }
      })
      .style('cursor', 'pointer')
      .on('mouseover', function(_event: any, d: any) {
        d3.select(this)
          .attr('stroke-width', 4)
          .attr('opacity', 0.8);
        setHoveredSegment(d.data);
      })
      .on('mouseout', function() {
        d3.select(this)
          .attr('stroke-width', 2)
          .attr('opacity', 1);
        setHoveredSegment(null);
      });

    // Add labels for groups (inner ring)
    const groupNodes = nodes.filter((d: any) => d.depth === 1);
    const labelArc = d3.arc<any>()
      .startAngle((d: any) => d.x0)
      .endAngle((d: any) => d.x1)
      .innerRadius((d: any) => (d.y0 + d.y1) / 2)
      .outerRadius((d: any) => (d.y0 + d.y1) / 2);

    // Helper function to wrap text into multiple lines
    const wrapText = (text: string, maxLength: number): string[] => {
      if (text.length <= maxLength) return [text];
      const words = text.split(' ');
      const lines: string[] = [];
      let currentLine = '';
      
      words.forEach(word => {
        if ((currentLine + word).length <= maxLength) {
          currentLine += (currentLine ? ' ' : '') + word;
        } else {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        }
      });
      if (currentLine) lines.push(currentLine);
      return lines.length > 0 ? lines : [text];
    };

    // Create group containers for labels (to support icons + text)
    const groupLabelContainers = g.selectAll('.group-label-container')
      .data(groupNodes)
      .enter()
      .append('g')
      .attr('class', 'group-label-container')
      .attr('transform', (d: any) => {
        const [x, y] = labelArc.centroid(d);
        const angle = (d.x0 + d.x1) / 2;
        return `translate(${x},${y}) rotate(${angle * 180 / Math.PI - 90})`;
      });

    // Add icon for purpose groupBy
    if (groupBy === 'purpose') {
      groupLabelContainers.each(function(d: any) {
        const container = d3.select(this);
        // groupKey is the label, need to find the actual key
        const purposeKey = getPurposeKeyFromLabel(d.data.groupKey);
        const purposeConfig = purposeKey ? purposeTypes[purposeKey] : null;
        if (purposeConfig && purposeConfig.icon) {
          container.append('text')
            .attr('class', 'group-icon')
            .attr('text-anchor', 'middle')
            .attr('x', 0)
            .attr('y', -12) // Position icon above text
            .attr('font-size', '20px')
            .text(purposeConfig.icon);
        }
      });
    }

    // Add text labels
    const groupLabels = groupLabelContainers.append('text')
      .attr('class', 'group-label')
      .attr('text-anchor', 'middle')
      .attr('fill', isDark ? '#ffffff' : '#000000')
      .attr('font-size', '16px')
      .attr('font-weight', '600');

    // Add tspan elements for multi-line text
    groupLabels.each(function(d: any) {
      const text = d3.select(this);
      const name = d.data.name;
      const maxCharsPerLine = 15; // Maximum characters per line
      const lines = wrapText(name, maxCharsPerLine);
      const lineHeight = 18; // Line height in pixels
      const iconOffset = groupBy === 'purpose' ? 8 : 0; // Adjust for icon if present
      const startY = iconOffset - (lines.length - 1) * lineHeight / 2; // Center the text vertically
      
      lines.forEach((line, i) => {
        text.append('tspan')
          .attr('x', 0)
          .attr('y', startY + i * lineHeight)
          .attr('text-anchor', 'middle')
          .text(line);
      });
    });

    // Add labels for keywords (outer ring) - only show if space allows
    const keywordNodes = nodes.filter((d: any) => d.depth === 2);
    const keywordLabelArc = d3.arc<any>()
      .startAngle((d: any) => d.x0)
      .endAngle((d: any) => d.x1)
      .innerRadius((d: any) => (d.y0 + d.y1) / 2)
      .outerRadius((d: any) => (d.y0 + d.y1) / 2 + 15);

    g.selectAll('.keyword-label')
      .data(keywordNodes.filter((d: any) => (d.x1 - d.x0) > 0.1)) // Only show labels for larger segments
      .enter()
      .append('text')
      .attr('class', 'keyword-label')
      .attr('transform', (d: any) => {
        const [x, y] = keywordLabelArc.centroid(d);
        const angle = (d.x0 + d.x1) / 2;
        return `translate(${x},${y}) rotate(${angle * 180 / Math.PI - 90})`;
      })
      .attr('text-anchor', 'middle')
      .attr('fill', isDark ? '#ffffff' : '#000000')
      .attr('font-size', '9px')
      .attr('font-weight', '500')
      .text((d: any) => {
        const name = d.data.name || d.data.keyword;
        return name.length > 10 ? `${name.substring(0, 10)}...` : name;
      });
  }, [sunburstData, isDark, groupBy]);

  // Download SVG function
  const downloadSVG = useCallback(() => {
    const svgElement = isFullscreen ? fullscreenSvgRef.current : svgRef.current;
    if (!svgElement) return;
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = svgUrl;
    downloadLink.download = `keywordyab-chart-${groupBy}-${Date.now()}.svg`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(svgUrl);
  }, [isFullscreen, groupBy]);

  // Expose downloadSVG to parent via callback ref
  useEffect(() => {
    if (onDownloadSVGRef) {
      onDownloadSVGRef(downloadSVG);
    }
  }, [downloadSVG, onDownloadSVGRef]);

  // Draw Sunburst chart for normal view
  useEffect(() => {
    if (!svgRef.current || isFullscreen) return;
    drawChart(svgRef.current, 1000, 1000);
  }, [drawChart, isFullscreen]);

  // Draw Sunburst chart for fullscreen view
  useEffect(() => {
    if (!fullscreenSvgRef.current || !isFullscreen) return;
    const width = Math.min(window.innerWidth - 100, 1400);
    const height = Math.min(window.innerHeight - 150, 1400);
    drawChart(fullscreenSvgRef.current, width, height);
  }, [drawChart, isFullscreen]);

  // Handle escape key to exit fullscreen
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        onToggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isFullscreen, onToggleFullscreen]);

  return (
    <>
      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div
          ref={fullscreenRef}
          className="fixed inset-0 z-[999999] bg-white dark:bg-gray-900 flex flex-col items-center justify-center p-8"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
        >
          {/* Close Button */}
          <button
            onClick={onToggleFullscreen}
            className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} p-2 rounded-md transition-colors ${
              isDark
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-tooltip-id="action-tooltip"
            data-tooltip-content={language === 'fa' ? 'بستن' : 'Close'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Fullscreen Chart */}
          <div className="w-full h-full flex flex-col items-center justify-center">
            <h3 className={`text-xl font-bold mb-4 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {language === 'fa' ? 'گروه‌بندی بر اساس ' : 'Grouped by '}
              {groupBy === 'intent' ? (language === 'fa' ? 'نیت' : 'Intent') :
               groupBy === 'difficulty' ? (language === 'fa' ? 'سختی' : 'Difficulty') :
               groupBy === 'searchVolume' ? (language === 'fa' ? 'حجم جستجو' : 'Search Volume') :
               groupBy === 'length' ? (language === 'fa' ? 'طول کلمه' : 'Length') :
               groupBy === 'purpose' ? (language === 'fa' ? 'هدف' : 'Purpose') :
               groupBy === 'source' ? (language === 'fa' ? 'منبع' : 'Source') : ''}
            </h3>
            <div className="flex-1 flex items-center justify-center w-full">
              <svg ref={fullscreenSvgRef} style={{ maxWidth: '100%', maxHeight: '100%' }}></svg>
            </div>
          </div>
        </div>
      )}

      {/* Normal View */}
      <div className={`w-full ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="flex flex-col items-center justify-center min-h-[800px]">
          {/* Sunburst Chart */}
          <div className="w-full max-w-7xl mb-8">
            <div className="flex justify-center">
              <svg ref={svgRef}></svg>
            </div>
          </div>

        {/* Tooltip */}
        {hoveredSegment && (
          <div
            ref={tooltipRef}
            className={`fixed p-4 rounded-lg shadow-lg border pointer-events-none z-50 ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
            style={{
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <p className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {hoveredSegment.keyword || hoveredSegment.name}
            </p>
            <div className={`text-sm space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {hoveredSegment.stats && (
                <>
                  <p>{language === 'fa' ? 'تعداد کلمات:' : 'Total Keywords:'} {hoveredSegment.stats.totalKeywords}</p>
                  {hoveredSegment.stats.avgDifficulty > 0 && (
                    <p>{language === 'fa' ? 'میانگین سختی:' : 'Avg Difficulty:'} {hoveredSegment.stats.avgDifficulty}</p>
                  )}
                  {hoveredSegment.stats.avgSearchVolume > 0 && (
                    <p>{language === 'fa' ? 'میانگین حجم جستجو:' : 'Avg Search Volume:'} {formatSearchVolume(hoveredSegment.stats.avgSearchVolume)}</p>
                  )}
                </>
              )}
              {hoveredSegment.keyword && (
                <>
                  {hoveredSegment.difficulty !== null && hoveredSegment.difficulty !== undefined && (
                    <p>{language === 'fa' ? 'سختی:' : 'Difficulty:'} {hoveredSegment.difficulty}</p>
                  )}
                  {hoveredSegment.searchVolume !== null && hoveredSegment.searchVolume !== undefined && (
                    <p>{language === 'fa' ? 'حجم جستجو:' : 'Search Volume:'} {formatSearchVolume(hoveredSegment.searchVolume)}</p>
                  )}
                  {hoveredSegment.intent && Array.isArray(hoveredSegment.intent) && hoveredSegment.intent.length > 0 && (
                    <p>{language === 'fa' ? 'نیت:' : 'Intent:'} {hoveredSegment.intent.map(i => {
                      const intent = intentTypes[i];
                      return intent ? (language === 'fa' ? intent.label : intent.enLabel) : i;
                    }).join(', ')}</p>
                  )}
                  {hoveredSegment.purpose && Array.isArray(hoveredSegment.purpose) && hoveredSegment.purpose.length > 0 && (
                    <p>{language === 'fa' ? 'هدف:' : 'Purpose:'} {hoveredSegment.purpose.map(p => {
                      const purpose = getPurposeStyle(p);
                      return purpose ? (language === 'fa' ? purpose.label : purpose.enLabel) : p;
                    }).join(', ')}</p>
                  )}
                  {hoveredSegment.parent && (
                    <p>{language === 'fa' ? 'گروه:' : 'Group:'} {hoveredSegment.parent}</p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default ChartView;


